import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { find } from 'ramda';
import Multilingual from '~~utils/Multilingual';
import Select from '~~elements/Select';
import Input from '~~elements/Input';
import Button from '~~elements/Button';
import Loading from '~~elements/Loading';
import { COLOR } from '~~elements/Const';
import { GameSectionDiv } from '../styled';
import { recordTypes, boxBattingColumns, boxPitchingColumns, boxFieldingColumns, fieldingPositions } from '../Const';
import MoveUpSvg from '../assets/move-up.svg';
import MoveDownSvg from '../assets/move-down.svg';

const StyledDiv = styled.div`
  width: 100%;
  .stats {
    &--title {
      font-size: 1.2rem;
      border-bottom: 1px solid ${COLOR.secondary};
      > button {
        margin: 0.1rem 0.2rem;
      }
    }
    &--record {
      width: 100%;
      .row {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        align-items: flex-end;
        padding: 0.2rem 0;
      }
    }
    &--batting {
      width: 100%;
      overflow-x: auto;
    }
    &--pitching {
      width: 100%;
      overflow-x: auto;
    }
    &--fielding {
      width: 100%;
      overflow-x: auto;
    }
  }

  .up-down-control {
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    border-radius: 50%;
    border: none;
    > img {
      padding: 0;
      margin: 0;
      width: 100%;
      height: 100%;
    }
  }

  table {
    border-collapse: collapse;
  }
  table,
  th,
  td {
    border: 1px solid #eee;
  }
  th,
  td {
    text-align: center;
    white-space: nowrap;
    padding: 0.2rem;
    min-width: 3rem;
    > input {
      padding: 0;
    }

    &:first-child {
      position: sticky;
      left: 0;
      background-color: #fff;
      z-index: 1;
    }
  }
`;

const playerInOption = (player) => `#${player.number} ${player.name}`;

const GameStatsSection = ({
  title,
  playerLoading,
  records,
  batters,
  pitchers,
  inGameplayers,
  teamPlayers,
  recordPbP,
  onChange,
  apiInProgress,
}) => {
  const [statsLang, setStatsLang] = useState('en');
  const [currentRecords, setCurrentRecords] = useState(records);
  const [currentBatters, setCurrentBatters] = useState(batters);
  const [currentPitchers, setCurrentPitchers] = useState(pitchers);
  const [currentInGamePlayers, setCurrentInGamePlayers] = useState(inGameplayers);

  useEffect(() => {
    setCurrentRecords(records);
  }, [records]);
  useEffect(() => {
    setCurrentBatters(batters);
  }, [batters]);
  useEffect(() => {
    setCurrentPitchers(pitchers);
  }, [pitchers]);
  useEffect(() => {
    setCurrentInGamePlayers(inGameplayers);
  }, [inGameplayers]);

  useEffect(() => {
    onChange({
      records: currentRecords,
      batters: currentBatters,
      pitchers: currentPitchers,
      inGameplayers: currentInGamePlayers,
    });
  }, [currentBatters, currentInGamePlayers, currentPitchers, currentRecords, onChange]);

  const handleRecordCreate = () => {
    const newRecords = currentRecords.slice(0);
    newRecords.push({
      player: {
        name: '',
        number: '',
        uniqid: '',
      },
      player_type: '',
      value: '',
      comment: '',
    });
    setCurrentRecords(newRecords);
  };

  const handleRecordChange = (i) => (e) => {
    switch (e.target.name) {
      case 'player': {
        const targetPlayer = find((p) => p.uniqid === e.target.value, teamPlayers);
        if (!targetPlayer) {
          return;
        }
        currentRecords[i].player = {
          name: targetPlayer.name,
          number: targetPlayer.number,
          uniqid: targetPlayer.uniqid,
        };
        break;
      }
      case 'playerType':
        currentRecords[i].player_type = e.target.value;
        currentRecords[i].value = '';
        currentRecords[i].comment = '';
        break;
      case 'value':
        currentRecords[i].value = e.target.value;
        currentRecords[i].comment = '';
        break;
      case 'comment':
        currentRecords[i].comment = e.target.value;
        break;
      default:
        throw new Error();
    }

    setCurrentRecords(currentRecords.slice(0));
  };

  const handleRecordDelete = (i) => () => {
    const newRecords = currentRecords.slice(0);
    newRecords.splice(i, 1);
    setCurrentRecords(newRecords);
  };

  const getRecordValueSelect = (record, i) => {
    if (!recordTypes[record.player_type]) {
      return <></>;
    }

    return (
      <Select
        name="value"
        label={Multilingual(`GAME.STATS.RECORD.VALUE.WORDING`)}
        value={record.value}
        disabled={apiInProgress}
        onChange={handleRecordChange(i)}
      >
        <option value="">{Multilingual(`FORM.PLEASE_SELECT`)}</option>
        {recordTypes[record.player_type].map((recordValue) => (
          <option key={recordValue} value={recordValue}>
            {Multilingual(`GAME.STATS.RECORD.VALUE.${recordValue}`)}
          </option>
        ))}
      </Select>
    );
  };

  const getRecordCommentSelect = (record, i) => {
    if (record.value !== 'SP_RECORD') {
      return <></>;
    }

    return (
      <Input
        name="comment"
        width="6rem"
        value={record.comment}
        disabled={apiInProgress}
        onChange={handleRecordChange(i)}
      />
    );
  };

  const switchBoxLang = () => {
    setStatsLang(statsLang === 'en' ? '' : 'en');
  };

  const handlePossibleFocusChange = (e) => {
    if (37 > e.keyCode || e.keyCode > 40) {
      return true;
    }

    e.preventDefault();

    const inputName = e.target.name;
    const currentTd = e.currentTarget.parentElement;
    const currentTr = currentTd.parentElement;
    if ([37, 39].includes(e.keyCode)) {
      let targetTd = null;
      switch (e.keyCode) {
        case 37: // left
          targetTd = currentTd.previousElementSibling;
          break;
        case 39: // right
          targetTd = currentTd.nextElementSibling;
          break;
        default:
          return false;
      }

      if (!targetTd) {
        return false;
      }
      const input = targetTd.querySelector('input');
      if (!input) {
        return false;
      }

      input.focus();
      input.select();
      return false;
    }

    let targetTr = null;
    switch (e.keyCode) {
      case 38: // up
        targetTr = currentTr.previousElementSibling;
        break;
      case 40: // down
        targetTr = currentTr.nextElementSibling;
        break;
      default:
        return false;
    }

    if (!targetTr) {
      return false;
    }
    const input = targetTr.querySelector(`input[name="${inputName}"]`);
    if (!input) {
      return false;
    }

    input.focus();
    input.select();
    return false;
  };

  const handleInGamePlayerChange = (handlingBatters, handlingPitchers) => {
    const newInGamePlayers = [];
    const appendInGamePlayers = (bps) => {
      bps.forEach((bp) => {
        if (!bp.player.uniqid) {
          return;
        }
        const newInGamePlayer = find((p) => p.info.uniqid === bp.player.uniqid, newInGamePlayers);
        if (newInGamePlayer) {
          return;
        }

        const newFielding = {};
        boxFieldingColumns.forEach((column) => {
          newFielding[column] = '0';
        });
        delete newFielding.player;
        newFielding.order = 1;

        newInGamePlayers.push({
          info: {
            ...bp.player,
          },
          fieldings: [newFielding],
        });
      });
    };
    appendInGamePlayers(handlingBatters);
    appendInGamePlayers(handlingPitchers);

    newInGamePlayers.forEach((newInGamePlayer) => {
      const currentInGamePlayer = find((p) => p.info.uniqid === newInGamePlayer.info.uniqid, currentInGamePlayers);
      if (!currentInGamePlayer || currentInGamePlayer.fieldings.length === 0) {
        return;
      }

      newInGamePlayer.fieldings = currentInGamePlayer.fieldings.slice(0);
    });
    setCurrentInGamePlayers(newInGamePlayers);
  };

  const resetPinch = (handlingBatters) => {
    const orderExist = {};
    handlingBatters.forEach((batter) => {
      if (!orderExist[batter.order.toString()]) {
        orderExist[batter.order.toString()] = true;
        batter.is_PH = false;
        return;
      }

      batter.is_PH = true;
    });
  };

  const handleBattingChange = (i) => (e) => {
    switch (e.target.name) {
      case 'player': {
        const targetPlayer = find((p) => p.uniqid === e.target.value, teamPlayers);
        if (!targetPlayer) {
          return;
        }
        currentBatters[i].player = {
          name: targetPlayer.name,
          number: targetPlayer.number,
          uniqid: targetPlayer.uniqid,
        };
        handleInGamePlayerChange(currentBatters, currentPitchers);
        break;
      }
      default:
        currentBatters[i][e.target.name] = e.target.value;
        break;
    }

    if (e.target.name === 'order') {
      resetPinch(currentBatters);
    }

    setCurrentBatters(currentBatters.slice(0));
  };

  const handleBatterMove = (i, move) => () => {
    const targetIndex = i + move;
    if (targetIndex < 0 || targetIndex >= currentBatters.length) {
      return;
    }

    const toSwap = {
      player: {
        ...currentBatters[targetIndex].player,
      },
      ...currentBatters[targetIndex],
    };
    currentBatters[targetIndex] = {
      player: {
        ...currentBatters[i].player,
      },
      ...currentBatters[i],
    };
    currentBatters[i] = toSwap;

    resetPinch(currentBatters);
    setCurrentBatters(currentBatters.slice(0));
  };

  const handleBatterDelete = (i) => () => {
    const newBatters = currentBatters.slice(0);
    newBatters.splice(i, 1);
    resetPinch(newBatters);
    setCurrentBatters(newBatters);
    handleInGamePlayerChange(newBatters, currentPitchers);
  };

  const handleBatterCreate = () => {
    const newBatter = {};
    boxBattingColumns.forEach((column) => {
      newBatter[column] = '0';
    });
    newBatter.player = { name: '', number: '', uniqid: '' };
    newBatter.is_PH = false;

    const newBatters = [...currentBatters, newBatter];
    setCurrentBatters(newBatters);
    handleInGamePlayerChange(newBatters, currentPitchers);
  };

  const getBatterTd = (batter, i, column) => {
    switch (column) {
      case 'player':
        return (
          <Select
            name="player"
            value={batter.player.uniqid}
            disabled={apiInProgress}
            onChange={handleBattingChange(i)}
            mini
            minWidth="7rem"
          >
            <option value="">{Multilingual(`FORM.PLEASE_SELECT`)}</option>
            {teamPlayers.map((teamPlayer) => (
              <option key={teamPlayer.uniqid} value={teamPlayer.uniqid}>
                {playerInOption(teamPlayer)}
              </option>
            ))}
          </Select>
        );
      case 'is_PH':
        return batter.is_PH ? <>V</> : <></>;
      default:
        return (
          <Input
            name={column}
            value={batter[column]}
            disabled={apiInProgress}
            onChange={handleBattingChange(i)}
            onKeyDown={handlePossibleFocusChange}
          />
        );
    }
  };

  const resetPitchingOrder = (handlingPitchers) => {
    handlingPitchers.forEach((pitcher, i) => {
      pitcher.order = i + 1;
    });
  };

  const handlePitchingChange = (i) => (e) => {
    switch (e.target.name) {
      case 'player': {
        const targetPlayer = find((p) => p.uniqid === e.target.value, teamPlayers);
        if (!targetPlayer) {
          return;
        }
        currentPitchers[i].player = {
          name: targetPlayer.name,
          number: targetPlayer.number,
          uniqid: targetPlayer.uniqid,
        };
        handleInGamePlayerChange(currentBatters, currentPitchers);
        break;
      }
      default:
        currentPitchers[i][e.target.name] = e.target.value;
        break;
    }

    setCurrentPitchers(currentPitchers.slice(0));
  };

  const handlePitcherMove = (i, move) => () => {
    const targetIndex = i + move;
    if (targetIndex < 0 || targetIndex >= currentPitchers.length) {
      return;
    }

    const toSwap = {
      player: {
        ...currentPitchers[targetIndex].player,
      },
      ...currentPitchers[targetIndex],
    };
    currentPitchers[targetIndex] = {
      player: {
        ...currentPitchers[i].player,
      },
      ...currentPitchers[i],
    };
    currentPitchers[i] = toSwap;

    resetPitchingOrder(currentPitchers);
    setCurrentPitchers(currentPitchers.slice(0));
  };

  const handlePitcherDelete = (i) => () => {
    const newPitchers = currentPitchers.slice(0);
    newPitchers.splice(i, 1);
    resetPitchingOrder(newPitchers);
    setCurrentPitchers(newPitchers);
    handleInGamePlayerChange(currentBatters, newPitchers);
  };

  const handlePitcherCreate = () => {
    const newPitcher = {};
    boxPitchingColumns.forEach((column) => {
      newPitcher[column] = '0';
    });
    newPitcher.player = { name: '', number: '', uniqid: '' };
    newPitcher.order = currentPitchers.length + 1;

    const newPitchers = [...currentPitchers, newPitcher];
    setCurrentPitchers(newPitchers);
    handleInGamePlayerChange(currentBatters, newPitchers);
  };

  const getPitcherTd = (pitcher, i, column) => {
    switch (column) {
      case 'player':
        return (
          <Select
            name="player"
            value={pitcher.player.uniqid}
            disabled={apiInProgress}
            onChange={handlePitchingChange(i)}
            mini
            minWidth="7rem"
          >
            <option value="">{Multilingual(`FORM.PLEASE_SELECT`)}</option>
            {teamPlayers.map((teamPlayer) => (
              <option key={teamPlayer.uniqid} value={teamPlayer.uniqid}>
                {playerInOption(teamPlayer)}
              </option>
            ))}
          </Select>
        );
      case 'order':
        return <>{pitcher.order.toString()}</>;
      default:
        return (
          <Input
            name={column}
            value={pitcher[column]}
            disabled={apiInProgress}
            onChange={handlePitchingChange(i)}
            onKeyDown={handlePossibleFocusChange}
          />
        );
    }
  };

  const resetFieldingOrder = (handlingFielder) => {
    handlingFielder.fieldings.forEach((fielding, i) => {
      fielding.order = i + 1;
    });
  };

  const handleFieldingChange = (fielderIndex, i) => (e) => {
    const targetFielding = currentInGamePlayers[fielderIndex].fieldings[i];
    targetFielding[e.target.name] = e.target.value;
    if (['PO', 'A', 'E'].includes(e.target.name)) {
      targetFielding.TC = parseInt(targetFielding.PO) + parseInt(targetFielding.A) + parseInt(targetFielding.E);
    }

    setCurrentInGamePlayers(currentInGamePlayers.slice(0));
  };

  const handleFieldingMove = (fielderIndex, i, move) => () => {
    const targetPlayer = currentInGamePlayers[fielderIndex];
    if (!targetPlayer) {
      return;
    }
    const targetIndex = i + move;
    if (targetIndex < 0 || targetIndex >= targetPlayer.fieldings.length) {
      return;
    }

    const toSwap = {
      ...targetPlayer.fieldings[targetIndex],
    };
    targetPlayer.fieldings[targetIndex] = {
      ...targetPlayer.fieldings[i],
    };
    targetPlayer.fieldings[i] = toSwap;

    resetFieldingOrder(targetPlayer);
    setCurrentInGamePlayers(currentInGamePlayers.slice(0));
  };

  const handleFieldingDelete = (fielderIndex, i) => () => {
    currentInGamePlayers[fielderIndex].fieldings.splice(i, 1);
    resetFieldingOrder(currentInGamePlayers[fielderIndex]);
    setCurrentInGamePlayers(currentInGamePlayers.slice(0));
  };

  const handleFieldingCreate = (fielderIndex) => () => {
    const newFielding = {};
    boxFieldingColumns.forEach((column) => {
      newFielding[column] = '0';
    });
    delete newFielding.player;
    newFielding.order = currentInGamePlayers[fielderIndex].fieldings.length + 1;
    currentInGamePlayers[fielderIndex].fieldings.push(newFielding);

    setCurrentInGamePlayers(currentInGamePlayers.slice(0));
  };

  const getFieldingTd = (fielder, fielderIndex, fielding, i, column) => {
    if (['DH', 'PH', 'PR'].includes(fielding.position) && !['player', 'order', 'position'].includes(column)) {
      return <></>;
    }

    switch (column) {
      case 'player':
        if (i !== 0) {
          return <></>;
        }
        return <>{playerInOption(fielder.info)}</>;
      case 'order':
        return <>{fielding.order.toString()}</>;
      case 'position':
        return (
          <Select
            name="position"
            value={fielding.position}
            disabled={apiInProgress}
            onChange={handleFieldingChange(fielderIndex, i)}
            mini
          >
            <option value="">{Multilingual(`FORM.PLEASE_SELECT`)}</option>
            {fieldingPositions.map((fieldingPosition) => (
              <option key={fieldingPosition} value={fieldingPosition}>
                {Multilingual(`STATS.POSITION.${fieldingPosition}`)}
              </option>
            ))}
          </Select>
        );
      case 'TC':
        return <>{fielding.TC.toString()}</>;
      case 'PB':
      //fallthrough
      case 'SB':
      //fallthrough
      case 'CS':
        if (fielding.position !== 'C') {
          return <></>;
        }
      //fallthrough
      default:
        return (
          <Input
            name={column}
            value={fielding[column]}
            disabled={apiInProgress}
            onChange={handleFieldingChange(fielderIndex, i)}
            onKeyDown={handlePossibleFocusChange}
          />
        );
    }
  };

  if (playerLoading) {
    return <Loading width="10rem" />;
  }

  return (
    <GameSectionDiv>
      <h3 className="section--title">{title}</h3>
      {recordPbP ? (
        <div className="section--description success">{Multilingual(`TOURNAMENT.STATS_PBP_UPLOADED_HINT`)}</div>
      ) : (
        <div className="section--description">{Multilingual(`TOURNAMENT.STATS_DESCRIPTION`)}</div>
      )}
      <StyledDiv>
        <h4 className="stats--title">{Multilingual(`GAME.STATS.RECORD.WORDING`)}</h4>
        <div className="stats--record">
          {currentRecords.map((currentRecord, i) => (
            <div key={i} className="row">
              <Select
                name="player"
                label={Multilingual(`GAME.STATS.RECORD.PLAYER`)}
                value={currentRecord.player.uniqid}
                disabled={apiInProgress}
                onChange={handleRecordChange(i)}
              >
                <option value="">{Multilingual(`FORM.PLEASE_SELECT`)}</option>
                {teamPlayers.map((teamPlayer) => (
                  <option key={teamPlayer.uniqid} value={teamPlayer.uniqid}>
                    {playerInOption(teamPlayer)}
                  </option>
                ))}
              </Select>
              <Select
                name="playerType"
                label={Multilingual(`GAME.STATS.RECORD.TYPE.WORDING`)}
                value={currentRecord.player_type}
                disabled={apiInProgress}
                onChange={handleRecordChange(i)}
              >
                <option value="">{Multilingual(`FORM.PLEASE_SELECT`)}</option>
                {Object.keys(recordTypes).map((recordType) => (
                  <option key={recordType} value={recordType}>
                    {Multilingual(`GAME.STATS.RECORD.TYPE.${recordType}`)}
                  </option>
                ))}
              </Select>
              {getRecordValueSelect(currentRecord, i)}
              {getRecordCommentSelect(currentRecord, i)}
              <Button width="unset" color="transparent" mini onClick={handleRecordDelete(i)}>
                {Multilingual(`COMMON.DELETE`)}
              </Button>
            </div>
          ))}
          <div className="row">
            <Button width="unset" color="transparentWithBorder" onClick={handleRecordCreate}>
              {Multilingual(`COMMON.CREATE`)}
            </Button>
          </div>
        </div>
        <h4 className="stats--title">
          {Multilingual(`GAME.STATS.BATTING.WORDING`)}
          <Button width="unset" color="transparentWithBorder" mini onClick={switchBoxLang}>
            {Multilingual(`COMMON.SWITCH_LANG`)}
          </Button>
        </h4>
        <div className="stats--batting">
          <table>
            <thead>
              <tr>
                {boxBattingColumns.map((battingColumn) => (
                  <th key={battingColumn}>
                    {Multilingual(`STATS.BATTER.${battingColumn.toUpperCase()}`, {}, statsLang)}
                  </th>
                ))}
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {currentBatters.map((batter, i) => (
                <tr key={i}>
                  {boxBattingColumns.map((battingColumn) => (
                    <td key={battingColumn}>{getBatterTd(batter, i, battingColumn)}</td>
                  ))}
                  <td>
                    {i !== 0 && (
                      <Button className="up-down-control" color="transparent" nowrap onClick={handleBatterMove(i, -1)}>
                        <img src={MoveUpSvg} alt="pre" />
                      </Button>
                    )}
                  </td>
                  <td>
                    {i !== currentBatters.length - 1 && (
                      <Button className="up-down-control" color="transparent" nowrap onClick={handleBatterMove(i, 1)}>
                        <img src={MoveDownSvg} alt="pre" />
                      </Button>
                    )}
                  </td>
                  <td>
                    <Button width="unset" color="transparent" mini nowrap onClick={handleBatterDelete(i)}>
                      {Multilingual(`COMMON.DELETE`)}
                    </Button>
                  </td>
                </tr>
              ))}
              <tr>
                <td>
                  <Button width="unset" color="transparent" mini nowrap onClick={handleBatterCreate}>
                    {Multilingual(`COMMON.CREATE`)}
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <h4 className="stats--title">
          {Multilingual(`GAME.STATS.PITCHING.WORDING`)}
          <Button width="unset" color="transparentWithBorder" mini onClick={switchBoxLang}>
            {Multilingual(`COMMON.SWITCH_LANG`)}
          </Button>
        </h4>
        <div className="stats--pitching">
          <table>
            <thead>
              <tr>
                {boxPitchingColumns.map((pitchingColumn) => (
                  <th key={pitchingColumn}>
                    {Multilingual(`STATS.PITCHER.${pitchingColumn.toUpperCase()}`, {}, statsLang)}
                  </th>
                ))}
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {currentPitchers.map((pitcher, i) => (
                <tr key={i}>
                  {boxPitchingColumns.map((pitchingColumn) => (
                    <td key={pitchingColumn}>{getPitcherTd(pitcher, i, pitchingColumn)}</td>
                  ))}
                  <td>
                    {i !== 0 && (
                      <Button className="up-down-control" color="transparent" nowrap onClick={handlePitcherMove(i, -1)}>
                        <img src={MoveUpSvg} alt="pre" />
                      </Button>
                    )}
                  </td>
                  <td>
                    {i !== currentPitchers.length - 1 && (
                      <Button className="up-down-control" color="transparent" nowrap onClick={handlePitcherMove(i, 1)}>
                        <img src={MoveDownSvg} alt="pre" />
                      </Button>
                    )}
                  </td>
                  <td>
                    <Button width="unset" color="transparent" mini nowrap onClick={handlePitcherDelete(i)}>
                      {Multilingual(`COMMON.DELETE`)}
                    </Button>
                  </td>
                </tr>
              ))}
              <tr>
                <td>
                  <Button width="unset" color="transparent" mini nowrap onClick={handlePitcherCreate}>
                    {Multilingual(`COMMON.CREATE`)}
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <h4 className="stats--title">
          {Multilingual(`GAME.STATS.FIELDING.WORDING`)}
          <Button width="unset" color="transparentWithBorder" mini onClick={switchBoxLang}>
            {Multilingual(`COMMON.SWITCH_LANG`)}
          </Button>
        </h4>
        <div className="stats--fielding">
          <table>
            <thead>
              <tr>
                {boxFieldingColumns.map((fieldingColumn) => (
                  <th key={fieldingColumn}>
                    {Multilingual(`STATS.FIELDER.${fieldingColumn.toUpperCase()}`, {}, statsLang)}
                  </th>
                ))}
                <th></th>
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {currentInGamePlayers.map((inGameplayer, pi) => (
                <Fragment key={pi}>
                  {inGameplayer.fieldings.map((fielding, fi) => (
                    <tr key={`${pi}-${fi}`}>
                      {boxFieldingColumns.map((fieldingColumn) => (
                        <td key={fieldingColumn}>{getFieldingTd(inGameplayer, pi, fielding, fi, fieldingColumn)}</td>
                      ))}
                      <td>
                        {fi !== 0 && (
                          <Button
                            className="up-down-control"
                            color="transparent"
                            nowrap
                            onClick={handleFieldingMove(pi, fi, -1)}
                          >
                            <img src={MoveUpSvg} alt="pre" />
                          </Button>
                        )}
                      </td>
                      <td>
                        {fi !== inGameplayer.fieldings.length - 1 && (
                          <Button
                            className="up-down-control"
                            color="transparent"
                            nowrap
                            onClick={handleFieldingMove(pi, fi, 1)}
                          >
                            <img src={MoveDownSvg} alt="pre" />
                          </Button>
                        )}
                      </td>
                      <td>
                        {inGameplayer.fieldings.length !== 1 && (
                          <Button width="unset" color="transparent" mini nowrap onClick={handleFieldingDelete(pi, fi)}>
                            {Multilingual(`COMMON.DELETE`)}
                          </Button>
                        )}
                      </td>
                      {fi !== inGameplayer.fieldings.length - 1 ? (
                        <td></td>
                      ) : (
                        <td>
                          <Button width="unset" color="transparent" mini nowrap onClick={handleFieldingCreate(pi)}>
                            {Multilingual(`COMMON.CREATE`)}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </StyledDiv>
    </GameSectionDiv>
  );
};

export default GameStatsSection;
